import {scrollbar} from './designSystem';
import {headerNav} from '../../components/AppHeaders/headerNavTokens';
import type {HeaderNavMenuPaperTokens, HeaderNavMenuTriggerTokens} from '../../components/AppHeaders/headerNavTokens';

type ThemeLike = {palette: {mode: string}};

export type GetMenuStylesOptions = {
  variant?: 'header-module' | 'context' | 'avatar';
  isBackgroundColorDifferent?: boolean;
  /** @deprecated Use `variant: 'avatar'` instead */
  symmetricPadding?: boolean;
};

const getTriggerPalette = (tokens: HeaderNavMenuTriggerTokens, theme: ThemeLike) =>
  theme.palette.mode === 'dark' ? tokens.dark : tokens.light;

const getPaperPalette = (paper: HeaderNavMenuPaperTokens, theme: ThemeLike) =>
  theme.palette.mode === 'dark' ? paper.dark : paper.light;

const resolveMenuStyleOptions = (
  options?: boolean | GetMenuStylesOptions,
  legacyOptions?: GetMenuStylesOptions,
): GetMenuStylesOptions => {
  if (typeof options === 'boolean') {
    return {
      isBackgroundColorDifferent: options,
      variant: legacyOptions?.symmetricPadding || legacyOptions?.variant === 'avatar' ? 'avatar' : 'context',
      ...legacyOptions,
    };
  }

  const resolved = options ?? {};

  if (resolved.symmetricPadding && !resolved.variant) {
    return {...resolved, variant: 'avatar'};
  }

  return resolved;
};

const buildMenuPaperSx = (
  paper: HeaderNavMenuPaperTokens,
  theme: ThemeLike,
  isBackgroundColorDifferent?: boolean,
) => {
  const palette = getPaperPalette(paper, theme);
  const backgroundColor = isBackgroundColorDifferent
    ? palette.backgroundColorAlt
    : palette.backgroundColor;

  return {
    backgroundColor,
    border: paper.border,
    borderRadius: paper.borderRadius,
    padding: paper.padding,
    boxShadow: paper.boxShadow,
    backgroundImage: 'none',
    minWidth: paper.minWidth,
    width: paper.width,
    mt: paper.marginTop,
  };
};

const buildMenuTriggerSx = (
  tokens: HeaderNavMenuTriggerTokens,
  open: boolean,
  theme: ThemeLike,
  isBackgroundColorDifferent?: boolean,
) => {
  const palette = getTriggerPalette(tokens, theme);
  const backgroundColor = open
    ? palette.openBackgroundColor
    : isBackgroundColorDifferent
      ? palette.backgroundColorAlt
      : palette.backgroundColor;
  const borderRadius = open ? tokens.openBorderRadius : tokens.closedBorderRadius;
  const boxShadow = open ? tokens.openBoxShadow : tokens.closedBoxShadow;
  const nestChromeInButton = tokens.nestChromeInButton !== false;
  const height = tokens.closedHeight
    ? open
      ? tokens.openHeight
      : tokens.closedHeight
    : undefined;

  const buttonSx = {
    color: palette.color,
    backgroundColor,
    ...(height && {height}),
    ...(nestChromeInButton && {
      borderRadius,
      border: 0,
      boxShadow,
      '&:hover': {
        backgroundColor: palette.hoverBackgroundColor,
        color: palette.color,
      },
    }),
  };

  if (nestChromeInButton) {
    return {'&.MuiButton-root': buttonSx};
  }

  return {
    '&.MuiButton-root': buttonSx,
    borderRadius,
    border: 0,
    boxShadow,
    '&:hover': {
      backgroundColor: palette.hoverBackgroundColor,
    },
  };
};

export const getHeaderMenuTriggerSx = (
  open: boolean,
  theme: ThemeLike,
  isBackgroundColorDifferent?: boolean,
) => buildMenuTriggerSx(headerNav.headerMenuTrigger, open, theme, isBackgroundColorDifferent);

export const getEmpionButtonStyles = (
  open: boolean,
  theme: ThemeLike,
  isBackgroundColorDifferent?: boolean,
) => buildMenuTriggerSx(headerNav.contextMenuTrigger, open, theme, isBackgroundColorDifferent);

export const getMenuStyles = (
  theme: ThemeLike,
  options?: boolean | GetMenuStylesOptions,
  legacyOptions?: GetMenuStylesOptions,
) => {
  const resolvedOptions = resolveMenuStyleOptions(options, legacyOptions);
  const paper = resolvedOptions.variant === 'avatar'
    ? headerNav.avatarMenuPaper
    : resolvedOptions.variant === 'header-module'
      ? headerNav.headerModuleMenuPaper
      : headerNav.contextMenuPaper;

  return {
    backgroundImage: 'none',
    '.MuiMenu-paper': buildMenuPaperSx(
      paper,
      theme,
      resolvedOptions.isBackgroundColorDifferent,
    ),
  };
};

export const getHeaderMenuListSx = (variant: 'header-module' | 'avatar') => {
  const paper = variant === 'avatar' ? headerNav.avatarMenuPaper : headerNav.headerModuleMenuPaper;

  if (paper.menuListPaddingY == null && paper.menuListPaddingX == null) {
    return undefined;
  }

  return {
    py: paper.menuListPaddingY,
    px: paper.menuListPaddingX,
  };
};

export const getAppHeaderMenuItemSx = () => {
  const {menuItem} = headerNav;

  const hoverBackground = {
    backgroundColor: menuItem.hoverBackgroundColor,
  };

  const sx: Record<string, unknown> = {
    borderRadius: menuItem.borderRadius,
    backgroundColor: menuItem.backgroundColor,
    color: menuItem.color,
    '&:hover': {
      ...hoverBackground,
      color: menuItem.color,
      '& .MuiListItemIcon-root': {
        color: menuItem.color,
      },
    },
  };

  if (menuItem.marginX != null) {
    sx.mx = menuItem.marginX;
  }

  if (menuItem.marginY != null) {
    sx.my = menuItem.marginY;
  }

  if (menuItem.paddingX != null) {
    sx.px = menuItem.paddingX;
  }

  if (menuItem.paddingY != null) {
    sx.py = menuItem.paddingY;
  }

  if (menuItem.minHeight != null) {
    sx.minHeight = menuItem.minHeight;
  }

  if (menuItem.minWidth) {
    sx.minWidth = menuItem.minWidth;
    sx.width = '100%';
  }

  return sx;
};

export const getAppHeaderMenuItemConfig = () => headerNav.menuItem;

export const getAppHeaderWrapperSx = (isTransparent?: boolean) => {
  if (isTransparent) {
    return {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      border: undefined,
      borderColor: undefined,
      boxShadow: 'none',
    };
  }

  const {wrapper} = headerNav;

  return {
    backgroundColor: wrapper.backgroundColor,
    backgroundImage: wrapper.backgroundImage,
    border: wrapper.border,
    borderColor: wrapper.borderColor,
    boxShadow: wrapper.boxShadow ?? 'none',
  };
};

export const getScrollbarGlobalStyles = () => ({
  '*': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${scrollbar.thumb} ${scrollbar.track}`,
  },
  '::-webkit-scrollbar': {
    width: '5px',
  },
  '::-webkit-scrollbar-track': {
    background: scrollbar.track,
    borderRadius: '8px',
  },
  '::-webkit-scrollbar-thumb': {
    background: scrollbar.thumb,
    borderRadius: '8px',
  },
  '::-webkit-scrollbar-thumb:hover': {
    background: scrollbar.thumbHover,
  },
});
